import React, { useMemo } from 'react';

import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, IconButton, List, ListItemButton, ListItemText, Typography } from '@mui/material';

import { ChatThread } from './AiChatContext';
import { getThreadPreview } from './chatThreadUtils';

interface AiChatConversationListProps {
  threads: Record<string, ChatThread>;
  activeThreadId: string | null;
  onSelect: (threadId: string) => void;
  onDelete: (threadId: string) => void;
  onNewChat: () => void;
}

function formatRelativeTime(timestamp: number): string {
  const diffMs = Date.now() - timestamp;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) {
    return 'just now';
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

export default function AiChatConversationList({
  threads,
  activeThreadId,
  onSelect,
  onDelete,
  onNewChat,
}: AiChatConversationListProps) {
  const sortedThreads = useMemo(() => Object.values(threads).sort((a, b) => b.updatedAt - a.updatedAt), [threads]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Button variant="text" size="small" startIcon={<AddCommentOutlinedIcon />} onClick={onNewChat}>
          New chat
        </Button>
      </Box>

      {sortedThreads.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ px: 2, py: 1.5, display: 'block', textAlign: 'center' }}
        >
          No history yet.
        </Typography>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <List dense disablePadding>
          {sortedThreads.map((thread) => {
            const isActive = thread.id === activeThreadId;

            return (
              <ListItemButton key={thread.id} selected={isActive} onClick={() => onSelect(thread.id)} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={thread.title}
                  secondary={
                    <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, mr: 1 }}>
                        {getThreadPreview(thread.messages)}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                        {formatRelativeTime(thread.updatedAt)}
                      </Typography>
                    </Box>
                  }
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      fontWeight: isActive ? 'bold' : 'normal',
                      noWrap: true,
                    },
                    secondary: { component: 'div' },
                  }}
                />
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(thread.id);
                  }}
                  title="Delete conversation"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
}
